import React, { Component } from "react";
import {
    FlexContainer,
    BackgroundContainer,
    PortalActionBar,
    PortalCaption,
    PortalMetricCard,
    PortalMetricLabel,
    PortalSectionTitle,
    PortalSurface,
    CustomiseStepButton,
    portalColors
} from "src/components";
import ImageSlider from "../components/ImageSlider";
import ColorPicker from "../components/ColorPicker";
import { CustomiseContext } from "../CustomiseContext";
import {
    ColorButtonsContainer
} from "src/scenes/Customise/forms/SelectColor";
import { PhoneContainer } from "src/scenes/Customise/forms/UploadLogo";
import GridBackground from "src/img/GridBackground.png";
import styled from "styled-components";
import { AppScreens } from "../components/ScreenUtils";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";
import UploadIconImg from "src/img/UploadIcon.png";

const OverviewShell = styled.div`
    flex: 1 1 500px;
    min-width: 300px;
`;

const OverviewContent = styled(PortalSurface)`
    display: grid;
    gap: 1rem;
    margin-bottom: 0;
`;

const OverviewSection = styled.div`
    display: grid;
    gap: 0.75rem;
`;

const AssetGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const IconStatusGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
    flex-wrap: wrap;
`;

const PreviewFrame = styled.div`
    width: ${p => p.width || "213px"};
    height: ${p => p.height || "64px"};
    display: grid;
    place-items: center;
    border: ${p => p.$hasAsset ? `1px solid ${portalColors.border}` : `1px dashed ${portalColors.borderStrong}`};
    border-radius: ${p => p.radius || "10px"};
    background: ${portalColors.surface};
    overflow: hidden;
`;

const Divider = styled.hr`
    width: 100%;
    height: 1px;
    margin: 0;
    border: none;
    background: ${portalColors.border};
`;

export const ICON_STATUS = {
    PENDING: "pending", 
    ACTIVE: "active", 
    REMOVE: "remove",
};

const ICON_STATUS_CONFIG = {
    [ICON_STATUS.PENDING]: { label: "Pending Approval", color: "var(--calendar-text, #171A22)" },
    [ICON_STATUS.ACTIVE]: { label: "Active", color: "#36BE24" },
    [ICON_STATUS.REMOVE]: { label: "Pending Removal", color: "#FF3B30" },
};

class Finish extends Component {
    static contextType = CustomiseContext;

    state = { imageHash: Date.now() };

    render() {
        const {
            backGroundColor,
            secondaryColor,
            primaryColor,
            companyName,
            colourPalette,
            currentScreen,
            currentForm,
            screenIds,
            leftScreen,
            rightScreen,
            assetLogoUrl,
            currentLogoUrl,
            assetIconUrl,
            employeeStartScreen,
            clientStartScreen
        } = this.context;
        const logoUrl = currentLogoUrl || assetLogoUrl;

        return (
            <FlexContainer mDirection="column" tDirection="column" direction="row" style={{ flexWrap: "wrap", gap: "2rem" }}>
                <OverviewShell>
                    <PortalSectionTitle>
                        Customise Overview
                    </PortalSectionTitle>

                    <OverviewContent padding="1rem">
                        <PortalCaption margin="0" maxWidth="530px">
                            Here is an overview of your custom Me Business app. Refer to the
                            device preview on the right for a visual representation of your custom 
                            app.
                        </PortalCaption>

                        <AssetGrid>
                            <BusinessLogoContainer assetLogoUrl={logoUrl} />
                            <CustomAppIconContainer assetIconUrl={assetIconUrl} icons={this.context.icons} imageHash={this.state.imageHash} />
                        </AssetGrid>

                        <SelectAppColours
                            backGroundColor={backGroundColor}
                            primaryColor={primaryColor}
                            secondaryColor={secondaryColor}
                        />

                        <StartingScreens
                            employeeStartScreen={employeeStartScreen}
                            clientStartScreen={clientStartScreen}
                        />

                        <Divider />

                        <PortalActionBar padding="0" border="transparent" shadow="none" radius="0" justifyContent="flex-start">
                            <CustomiseStepButton
                                $variant="primary"
                                onClick={() => this.context.goToStep1()}
                            >
                                Edit
                            </CustomiseStepButton>
                        </PortalActionBar>
                    </OverviewContent>
                </OverviewShell>
                <PhoneContainer>
                    <ImageSlider
                        companyName={companyName}
                        logoTopOffset={45}
                        phoneFrameTopOffset={30}
                        currentScreen={currentScreen}
                        currentForm={currentForm}
                        screenIds={screenIds}
                        leftScreen={leftScreen}
                        rightScreen={rightScreen}
                        colourPalette={colourPalette}
                        assetLogoUrl={logoUrl}
                    />
                </PhoneContainer>
            </FlexContainer>
        );
    }
}

class BusinessLogoContainer extends Component {
    render() {
        const { assetLogoUrl } = this.props;

        return (
            <PortalMetricCard minHeight="auto" padding="0.75rem">
                <PortalMetricLabel>Business Logo</PortalMetricLabel>
                <FlexContainer width="160px" height="48px" alignItems="center">
                    <PreviewFrame width="160px" height="48px" $hasAsset={assetLogoUrl.length > 0}>
                        <BackgroundContainer
                            width="160px"
                            height="48px"
                            image={GridBackground}
                            style={{ alignItems: "center", border: "none", display: "flex", justifyContent: "center" }}
                        >
                            <img
                                style={{ maxWidth: "160px", maxHeight: "48px", objectFit: "contain" }}
                                src={assetLogoUrl}
                                alt={assetLogoUrl}
                            />
                        </BackgroundContainer>
                    </PreviewFrame>
                </FlexContainer>
            </PortalMetricCard>
        );
    }
}

class CustomAppIconContainer extends Component {
    groupIconsByStatus(icons = []) {
        return icons.reduce((acc, icon) => {
            let status = icon.status || ICON_STATUS.PENDING;
            if (!acc[status]) acc[status] = [];
            acc[status].push(icon);
            return acc;
        }, {});
    }

    render() {
        const { icons = [], imageHash } = this.props;

        const groupedIcons = this.groupIconsByStatus(icons);

        const pendingIcons = groupedIcons.pending || [];
        const removeIcons = groupedIcons.remove || [];
        const activeIcons = groupedIcons.active || [];

        const visibleStatuses = [
            { status: ICON_STATUS.PENDING, icons: pendingIcons },
            { status: ICON_STATUS.REMOVE, icons: removeIcons },
            { status: ICON_STATUS.ACTIVE, icons: activeIcons },
        ].filter(item => item.icons.length > 0);

        const hasAnyIcons =
            pendingIcons.length > 0 ||
            removeIcons.length > 0 ||
            activeIcons.length > 0;
            
        return (
            <PortalMetricCard minHeight="auto" padding="0.75rem">
                <PortalMetricLabel>
                    Custom App Icons{!hasAnyIcons ? <span style={{ color: "var(--calendar-text-muted, #969696)", fontWeight: "500" }}>: Not Available </span> : ""}
                </PortalMetricLabel>
                {!hasAnyIcons ? (
                    <PreviewFrame width="48px" height="48px">
                        <BackgroundContainer
                            height="100%"
                            image={UploadIconImg}
                            size="contain"
                            style={{
                                border: "none",
                                backgroundPosition: "center"
                            }}
                        />
                    </PreviewFrame>
                ) : (
                    <IconStatusGrid>
                        {visibleStatuses.map(item => (
                            <StatusColumn
                                key={item.status}
                                label={ICON_STATUS_CONFIG[item.status].label}
                                color={ICON_STATUS_CONFIG[item.status].color}
                                icons={item.icons}
                                imageHash={imageHash}
                            />
                        ))}
                    </IconStatusGrid>
                )}
            </PortalMetricCard>
        );
    }
}

class StatusColumn extends Component {
    render() {
        const { label, color, icons = [], imageHash } = this.props;

        const realIcons = icons.filter(Boolean);
        const showPlaceholder = realIcons.length === 0;

        return (
            <div style={{ width: "100%", minHeight: 0, display: "grid", gridTemplateColumns: "9rem minmax(0, 1fr)", alignItems: "center", gap: "0.65rem" }}>
                <PortalMetricLabel
                    color={color}
                    style={{ marginBottom: 0, whiteSpace: "nowrap" }}
                >
                    {label}
                </PortalMetricLabel>

                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "6px",
                        marginTop: 0
                    }}
                >
                    {showPlaceholder ? (
                        <PreviewFrame width="48px" height="48px">
                            <BackgroundContainer
                                height="100%"
                                image={UploadIconImg}
                                size="contain"
                                style={{
                                    border: "none",
                                    backgroundPosition: "center"
                                }}
                            />
                        </PreviewFrame>
                    ) : (
                        realIcons.map((icon, idx) => (
                            <PreviewFrame
                                key={idx}
                                width="48px"
                                height="48px"
                                $hasAsset
                            >
                                <BackgroundContainer
                                    height="100%"
                                    image={`${icon.iconUrl || icon.preview}?${imageHash}`}
                                    size="contain"
                                    style={{
                                        border: "none",
                                        backgroundPosition: "center"
                                    }}
                                />
                            </PreviewFrame>
                        ))
                    )}
                </div>
            </div>
        );
    }
}

class SelectAppColours extends Component {
    render() {
        const {
            backGroundColor,
            primaryColor,
            secondaryColor
        } = this.props;

        return (
            <OverviewSection>
                <PortalSectionTitle size="0.95rem">Colour Selection</PortalSectionTitle>

                <ColorButtonsContainer>
                    <div style={{ marginRight: "20px" }}>
                        <ColorPicker
                            isPreview={true}
                            color={backGroundColor}
                            handleColorChange={this.handleBackgroundChange}
                            height="57px"
                            width="57px"
                        />
                    </div>

                    <div style={{ marginRight: "20px" }}>
                        <ColorPicker
                            isPreview={true}
                            color={primaryColor}
                            handleColorChange={this.handlePrimaryColorChange}
                            height="57px"
                            width="57px"
                        />
                    </div>

                    <ColorPicker
                        isPreview={true}
                        color={secondaryColor}
                        handleColorChange={this.handleSecondaryColor}
                        height="57px"
                        width="57px"
                    />
                </ColorButtonsContainer>
            </OverviewSection>
        );
    }
}

class StartingScreens extends Component {
    render() {
        const {
            employeeStartScreen,
            clientStartScreen
        } = this.props;

        return (
            <OverviewSection>
                <PortalSectionTitle size="0.95rem">App Starting Screens</PortalSectionTitle>
                <AssetGrid>
                    <PortalMetricCard minHeight="auto" padding="0.85rem">
                        <PortalMetricLabel>{capitalizeString(pluralize(customNames.employees))}</PortalMetricLabel>
                        <PortalCaption margin="0">
                            {employeeStartScreen === AppScreens.QUOTES ? `${employeeStartScreen} (Default)` : employeeStartScreen}
                        </PortalCaption>
                    </PortalMetricCard>

                    <PortalMetricCard minHeight="auto" padding="0.85rem">
                        <PortalMetricLabel>{capitalizeString(pluralize(customNames.clients))}</PortalMetricLabel>
                        <PortalCaption margin="0">
                            {clientStartScreen === AppScreens.QUOTES ? `${clientStartScreen} (Default)` : clientStartScreen}
                        </PortalCaption>
                    </PortalMetricCard>
                </AssetGrid>
            </OverviewSection>
        );
    }
}

export default Finish;
